import axios from 'axios';
// --- external imports ------

import store from '~/store';
import firebase from '~/authentication/lib/firebase';
import { uiActions } from '~/store';
import { MAIN_SCREEN_MODALS } from '~/lib/constants';
import { NON_RETRY_REQUESTS } from '../endpoints';
import { clearSessionData, clearGameplayData } from '~/lib/sessionCleanup';
import resetAnalyticsIdentity from '~/analytics/identity';
import { FORBIDDEN, NOT_FOUND } from './httpStatuses';

function extractEndpointAction(url) {
    try {
        // Remove trailing ?query=... if present
        const noQuery = url.split('?')[0];
        // Remove trailing slash if present (for consistency)
        const clean = noQuery.replace(/\/$/, '');
        // Take last segment
        const segs = clean.split('/');
        return segs[segs.length - 1];
    } catch {
        return url;
    }
}
// --- internal imports ------

// How long the forced logout waits for Firebase to drop its credential before it
// navigates regardless. Long enough that the storage write is never cut off in
// practice, short enough that a blocked IndexedDB cannot hold the player on a
// screen they have already been logged out of.
const SIGN_OUT_DEADLINE_MS = 2000;

class AxiosInterceptorService {
	constructor() {
		this.retryCount = 3;
		this.retryDelay = 1000;
		this.maxRetryDelay = 10000;
		this.retryableStatusCodes = [408, 429];
		this.retryableErrorCodes = ['ECONNABORTED', 'ENOTFOUND', 'ECONNRESET', 'ETIMEDOUT'];
		this.isNetworkErrorModalShown = false;
		this.leavingTheSave = null;

		// --- State for robust request dependency management ---
		// A key names a kind of request, not one request: two sales can be in
		// flight under 'SELL_PRODUCT' at once, and on a slow connection they are.
		// So each tracked request gets its own ticket, the controller that hands
		// its response back is held under that ticket, and a key maps to every
		// request still in flight under it. Held one-per-key, the second sale
		// overwrote the first's controller: the first response was handed to the
		// second caller, the second response to nobody, and the first caller
		// waited forever — one inbox notification for two sales.
		this.activeRequests = new Map(); // requestKey -> Set of in-flight promises
		this.failedRequests = new Set();
		this.requestControllers = new Map(); // ticket -> { resolve, promise }
		this.lastTicket = 0;
	}

	async request(config) {
		const { waitFor, requestKey } = config;

		if (waitFor && waitFor.length > 0) {
			console.log(`[REQUEST_GATE] Request for '${requestKey || config.url}' is waiting for:`, waitFor);

			const dependencies = waitFor.flatMap(key =>
				[...(this.activeRequests.get(key) || [])].map(promise => ({ key, promise }))
			);

			// Read from what the dependencies ended with, not only from the flag.
			// The flag is one per key and the last request to finish sets it, so
			// when one sale fails and another under the same key succeeds after
			// it, the success clears the failure while we are still waiting on
			// both — and the turn would close over a sale that never went through.
			// A result cannot be overwritten. The flag still answers for failures
			// that finished before this request started waiting.
			const results = await Promise.allSettled(dependencies.map(({ promise }) => promise));
			const failedWhileWaiting = dependencies.find((_, index) => results[index].value?.__isError);

			const failedDependency = failedWhileWaiting?.key
				?? waitFor.find(key => this.failedRequests.has(key));
			if (failedDependency) {
				const errorMessage = `[REQUEST_GATE] Did not execute '${requestKey || config.url}' because dependency '${failedDependency}' failed.`;
				console.error(errorMessage);

				// --- MODIFIED: Return a resolved error response for dependency failure ---
				return Promise.resolve({
					data: { message: errorMessage, dependency: failedDependency },
                    status: 424, // Failed Dependency
                    statusText: 'Failed Dependency',
                    headers: {},
                    config: config,
					__isError: true,
					__isDependencyFailure: true,
				});
			}
			console.log(`[REQUEST_GATE] Dependencies for '${requestKey || config.url}' have completed successfully. Proceeding.`);
		}

		if (requestKey) {
			let resolver;
			const deferredPromise = new Promise((resolve) => {
				resolver = resolve;
			});

			const requestTicket = ++this.lastTicket;
			this.requestControllers.set(requestTicket, { resolve: resolver, promise: deferredPromise });
			if (!this.activeRequests.has(requestKey)) {
				this.activeRequests.set(requestKey, new Set());
			}
			this.activeRequests.get(requestKey).add(deferredPromise);

			// Fire and forget; interceptor will resolve our promise.
			axios({ ...config, requestTicket });

			return deferredPromise;
		} else {
			// For non-tracked requests, we need to manually catch to prevent unhandled rejections
			return axios(config).catch(error => {
				return this.formatErrorAsResponse(error);
			});
		}
	}

    // --- NEW: Helper to format an error into a response-like object ---
    formatErrorAsResponse(error) {
        if (error.response) {
            // This is a standard HTTP error (e.g., 404, 500)
            return {
                data: error.response.data || { message: error.message },
                status: error.response.status,
                statusText: error.response.statusText,
                headers: error.response.headers,
                config: error.config,
                __isError: true // Flag for easy identification in UI
            };
        } else {
            // This is a network error (e.g., timeout, DNS issue)
            return {
                data: { message: error.message, code: error.code },
                status: 599, // Using a custom status for network errors
                statusText: 'Network Error',
                headers: {},
                config: error.config,
                __isError: true
            };
        }
    }

	shouldRetry(error, attempt) {
		const config = error.config || {};
		const endpointAction = config.url ? extractEndpointAction(config.url) : null;
		if (endpointAction && NON_RETRY_REQUESTS.includes(endpointAction)) return false;
		console.log(`[SHOULD_RETRY] attempt=${attempt}, maxRetries=${this.retryCount}, status=${error.response?.status}, code=${error.code}`);
		
		if (attempt >= this.retryCount) return false;
		if (error.response?.status === 401) return false;

		if (error.response?.status >= 400 && error.response?.status < 500) {
			return this.retryableStatusCodes.includes(error.response.status);
		}
		if (error.response?.status >= 500) return true;
		if (error.code && this.retryableErrorCodes.includes(error.code)) return true;
		if (!error.response) return true;

		return false;
	}

	calculateDelay(attempt) {
		const delay = this.retryDelay * Math.pow(2, attempt - 1);
		return Math.min(delay, this.maxRetryDelay);
	}

	// Only the session goes. The playthrough stays on disk — a 401 is not always a
	// real logout, and an expired token should not cost the player their save. The
	// reasoning, and the list a restart *does* drop, are in `~/lib/sessionCleanup`.
	//
	// Awaited, and in this order. `firebase.signOut()` is a promise whose work is a
	// write to the browser storage holding the credential, and this used to be fired
	// off unawaited with `window.location.href` assigned on the very next line. A
	// navigation is free to tear the page down before that write commits, so the
	// Firebase session could outlive the logout that was supposed to end it — and on
	// the load that follows, `onAuthStateChanged` reports a signed-in player,
	// `UserProvider` mints a fresh token, `AuthRoute` sends them to `/game/`, and
	// whatever raised the 401 raises it again. That is the difference between a
	// player being logged out and a login page that flashes past them in a loop with
	// no human in it, which is how this arrives in a bug report.
	//
	// The stored token goes first and synchronously: it is what the guarded routes
	// read, so the session is over from the app's point of view before anything is
	// awaited, whatever becomes of the rest.
	async handleUnauthorized() {
		console.warn('Unauthorized request detected. Logging out user...');
		clearSessionData();

		// A forced logout is still a logout, so the analytics identity goes with the
		// rest of the session. Left behind, it files the events and the session
		// recording of whoever signs in on the login screen below under the player
		// this just threw out. Above the await for the same reason the stored token
		// is: done synchronously, before anything that can wait or navigate.
		resetAnalyticsIdentity();

		store.dispatch({ type: 'GAME_RESET' });

		try {
			await this.signOutOfFirebase();
		}
		catch (error) {
			// The token is already gone, so the player is logged out of this app
			// either way. A sign-out that failed must not strand them on a game
			// screen with no session and no navigation.
			console.error('Failed to sign out of Firebase.', error);
		}

		// `replace`, not `href`: the screen behind us is one that just answered 401,
		// so leaving it on the history stack only offers the player a Back button
		// that walks into the same failure. The two deliberate logout paths
		// (`game-menu`, developer options `LoginStatus`) already use `replace`.
		window.location.replace('/login');
	}

	// Firebase's sign-out is local — it drops the stored credential and notifies its
	// listeners, with no request to make — so under any normal condition this
	// settles in a few milliseconds. It reaches IndexedDB to do it, though, and
	// IndexedDB can block indefinitely behind another tab. Waiting forever there
	// would leave the player looking at a frozen game screen, which is worse than
	// the race this bounds: past the deadline we navigate anyway and are no worse
	// off than before.
	signOutOfFirebase() {
		return Promise.race([
			firebase.signOut(),
			new Promise((resolve) => setTimeout(resolve, SIGN_OUT_DEADLINE_MS))
		]);
	}

	// A 403 means the save is not this player's: signed in as someone else in
	// another tab, or on a machine someone else played on. No retry or refresh
	// changes that, so the playthrough goes and the player is sent to character
	// select to pick a game they can open — from any request, not only the load.
	// Several requests in flight can be refused together; they share one exit.
	handleForbidden() {
		if (!this.leavingTheSave) {
			this.leavingTheSave = this.leaveTheSave();
		}
		return this.leavingTheSave;
	}

	async leaveTheSave() {
		console.warn('Request refused for this save. Leaving it for character select...');
		await clearGameplayData();
		store.dispatch({ type: 'GAME_RESET' });
		window.location.href = '/game/select-character';
	}

	// A status the app answers itself is not a network problem, and must not be
	// dressed as one. Only the load claims a 404, by setting
	// `isNotFoundHandledByCaller`: a save that is gone is let go of there and the
	// player sent to character select. A 404 anywhere else is a request the
	// backend does not know, and gets the modal like any other failure, so the
	// player refreshes rather than carrying on over it.
	isAnsweredByTheCaller(error) {
		return error?.response?.status === NOT_FOUND
			&& error?.config?.isNotFoundHandledByCaller === true;
	}

	handleNetworkError(error) {
		if (this.isAnsweredByTheCaller(error)) return;
		if (this.isNetworkErrorModalShown) return;
		console.error('Network error after all retries failed:', error);
		this.isNetworkErrorModalShown = true;
		store.dispatch(uiActions.changeActiveModal(MAIN_SCREEN_MODALS.NETWORK_ERROR));
	}

	createRetryInterceptor() {
		return async (error) => {
			const config = error.config;
			if (!config) return this.formatErrorAsResponse(error);

			const requestSignature = `${config.method?.toUpperCase()}_${config.url}`;
			// Carried on the request itself. Held in a map under the URL, the count
			// was shared by every request to that URL and never cleared once a
			// request finished, so a URL that had used up its retries once was
			// never retried again for the rest of the session.
			const currentRetryCount = config.retryAttempt || 0;

			// Handle 401 as a permanent, non-retryable failure
			if (error.response?.status === 401) {
				const formattedResponse = this.formatErrorAsResponse(error);
				this.settleTrackedRequest(config, formattedResponse, true); // Resolve with error object
				// Awaited: this interceptor is what hands the result back to the
				// caller, and several of them navigate on it. Letting the logout run
				// unattended behind that is the same race again, one level up.
				await this.handleUnauthorized();
				return Promise.resolve(formattedResponse);
			}

			// Not retried and no modal: see `handleForbidden`.
			if (error.response?.status === FORBIDDEN) {
				const formattedResponse = this.formatErrorAsResponse(error);
				this.settleTrackedRequest(config, formattedResponse, true);
				await this.handleForbidden();
				return formattedResponse;
			}

			if (this.shouldRetry(error, currentRetryCount)) {
				const retryAttempt = currentRetryCount + 1;
				const delay = this.calculateDelay(retryAttempt);
				console.warn(`[INTERCEPTOR] Retrying '${config.requestKey || requestSignature}' in ${delay}ms (attempt ${retryAttempt}/${this.retryCount})`);
				await new Promise(resolve => setTimeout(resolve, delay));
				return axios({ ...config, retryAttempt });
			}

			// --- PERMANENT FAILURE LOGIC (MODIFIED) ---
			console.error(`[INTERCEPTOR] All retries failed for '${config.requestKey || requestSignature}'.`);
            const formattedResponse = this.formatErrorAsResponse(error);

			// Resolve the master deferred promise with the formatted error object,
			// still marking the key as failed for dependency checks
			this.settleTrackedRequest(config, formattedResponse, true);
			
			this.handleNetworkError(error);
			// Resolve the promise for the original caller with the formatted error
			return Promise.resolve(formattedResponse);
		};
	}

	// Hands a tracked request's result to the caller that is waiting on it, and
	// only that caller.
	settleTrackedRequest(config, response, hasFailed) {
		const { requestKey, requestTicket } = config;
		const controller = this.requestControllers.get(requestTicket);
		if (!requestKey || !controller) return;

		controller.resolve(response);
		if (hasFailed) {
			this.failedRequests.add(requestKey);
		}
		else {
			this.failedRequests.delete(requestKey);
		}

		const inFlight = this.activeRequests.get(requestKey);
		inFlight?.delete(controller.promise);
		if (inFlight?.size === 0) {
			this.activeRequests.delete(requestKey);
		}
		this.requestControllers.delete(requestTicket);
	}

	setupInterceptors() {
		axios.interceptors.request.use(
			(config) => config,
			(error) => Promise.reject(error)
		);

		axios.interceptors.response.use(
			(response) => {
				this.settleTrackedRequest(response.config, response, false);
				return response;
			},
			this.createRetryInterceptor()
		);
	}

	resetNetworkErrorModal() {
		this.isNetworkErrorModalShown = false;
		this.failedRequests.clear();
		console.log('[INTERCEPTOR] Network error modal flag reset');
	}

	init() {
		this.setupInterceptors();
		console.log('Axios interceptor service initialized [Error-Returning Mode]');
	}
}

const ApiService = new AxiosInterceptorService();
export default ApiService;
